import React, { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigationType } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import { PrincipalContextProvider } from './context/PrincipalContext';
import { EntitlementsProvider } from './context/EntitlementsContext';
import { PermissionsProvider } from './context/PermissionsContext';
import { setSessionRefusedHandler, setTokenGetter } from './api/client';
import LoginPage from './auth/LoginPage';
import RecoveryCodeNotice from './auth/RecoveryCodeNotice';
import { CadenceThemeProvider } from './theme/ThemeContext';
import AppLayout from './components/AppLayout';
import DashboardPage from './pages/DashboardPage';
import DashboardBriefingPage from './pages/DashboardBriefingPage';
import UnifiedPlanPage from './pages/UnifiedPlanPage';
import EaLandingPage from './pages/EaLandingPage';
import { useDashboardVariant } from './hooks/useDashboardVariant';
import { personToday, useNewToday, useTodayPrefs } from './hooks/useTodayPrefs';
import { OldTodayOr } from './pages/today/TryNewToday';
import CalendarPage from './pages/CalendarPage';
import EAWorkspacePage from './pages/EAWorkspacePage';
import SignupPage from './pages/SignupPage';
import OnboardingSuccessPage from './pages/OnboardingSuccessPage';
import OAuthCallback from './auth/OAuthCallback';
import MagicLinkCallback from './auth/MagicLinkCallback';
import ConfirmMicrosoftPage from './auth/ConfirmMicrosoftPage';
import MfaSetupPage from './pages/MfaSetupPage';
import PasswordResetPage from './pages/PasswordResetPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ExecutiveProfilesPage from './pages/assistant/ExecutiveProfilesPage';
import DriversPage from './pages/drivers/DriversPage';
import FeatureRequestPage from './pages/FeatureRequestPage';
import ProjectsPage from './pages/ProjectsPage';
import ProjectDetailPage from './pages/ProjectDetailPage';
import DecisionsPage from './pages/DecisionsPage';
import OAuthAuthorizePage from './pages/OAuthAuthorizePage';
import DesktopAuthConsentPage from './pages/DesktopAuthConsentPage';
import { ErrorBoundary } from './components/ErrorBoundary';
import ApiKeyDeprecationPage from './pages/ApiKeyDeprecationPage';
import WelcomePage from './auth/WelcomePage';
import { legacyRedirectRoutes, setupEditTarget } from './navigation/legacyRedirects';

// WorkItemsPage is ~1500 LOC; lazy-loaded so a user landing on the
// dashboard doesn't pay for its bundle until they navigate to /work-items.
const WorkPage = lazy(() => import('./pages/work/WorkPage'));
// Used once per person, so it stays out of the main bundle.
const OnboardingWizardPage = lazy(() => import('./pages/OnboardingWizardPage'));
// Settings: everything about you, at /settings/:section.
const SettingsPage = lazy(() => import('./pages/settings/SettingsPage'));
const JoinPage = lazy(() => import('./pages/JoinPage'));

// Admin / platform / prompt-management pages are lazy-loaded. The executive
// majority never opens them, so shipping code they will not run is dead
// weight on first paint.
// Organisation: People, plan and billing, security, integrations (admins), Teams (members).
const OrganisationPage = lazy(() => import('./pages/organisation/OrganisationPage'));
const PlatformOverviewPage = lazy(() => import('./pages/PlatformOverviewPage'));
const PlatformTenantsPage = lazy(() => import('./pages/PlatformTenantsPage'));
const PlatformProductsPage = lazy(() => import('./pages/PlatformProductsPage'));
const PlatformEmailPage = lazy(() => import('./pages/PlatformEmailPage'));
const PlatformSecurityPage = lazy(() => import('./pages/PlatformSecurityPage'));
const PlatformBugReportsPage = lazy(() => import('./pages/PlatformBugReportsPage'));
const PlatformBillingPage = lazy(() => import('./pages/PlatformBillingPage'));
const PlatformReleasesPage = lazy(() => import('./pages/PlatformReleasesPage'));
// Focus carries Capacity, which most people never open.
const FocusPage = lazy(() => import('./pages/focus/FocusPage'));
// Reports and Reflections, with their charts: lazy, like Reports was large.
const InsightsPage = lazy(() => import('./pages/insights/InsightsPage'));
const ReviewPage = lazy(() => import('./pages/review/ReviewPage'));
const CloseDayPage = lazy(() => import('./pages/CloseDayPage'));
// One Today (web app v4 Phase 4): assistants, and anyone trying it.
const TodayPage = lazy(() => import('./pages/today/TodayPage'));
const BriefPage = lazy(() => import('./pages/BriefPage'));

function AuthGate({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, ensureValidToken, renewAfterRefusal } = useAuth();

  // The API client sends this session only while someone is signed in here.
  // Leaving the signed-in app (in-app navigation to a public page mounts a
  // separate tree) or signing out takes it away again, so nothing called
  // afterwards carries the previous person's token.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    setTokenGetter(ensureValidToken);
    setSessionRefusedHandler(renewAfterRefusal ?? null);
    return () => {
      setTokenGetter(null);
      setSessionRefusedHandler(null);
    };
  }, [isAuthenticated, ensureValidToken, renewAfterRefusal]);

  if (!isAuthenticated) {
    return <LoginPage />;
  }

  // After a recovery-code sign-in: how many are left and what to do next.
  return <>{children}<RecoveryCodeNotice /></>;
}

// Route "/" based on user role, whether they're trying the new Today, and
// the dashboard variant preference.

// Role keys are case-insensitive so a backend that ever lowercases
// the role string (or a cached session from an earlier app version)
// still routes correctly.
function normalizedRole(role: string | undefined | null): string {
  return (role ?? '').trim().toLowerCase();
}
function isPlatformAdmin(user: { role?: string } | null | undefined): boolean {
  return normalizedRole(user?.role) === 'platformadmin';
}
// CLASSIFICATION (what surface do you live on?) — role string only.
// Do NOT include the `isTenantAdmin` permission flag here. The flag means
// "you can do tenant-admin THINGS" (manage users, billing, etc.); it does
// NOT mean "your home page is the admin dashboard". Executives who also
// own the tenant (typical for the platform-owner-as-executive case) have
// role=Executive AND isTenantAdmin=true — they still get the Brief.
// Bug 2026-06-01: prior version conflated the flag with the role, locking
// Executive-with-admin-perms users out of /brief both on login redirect
// and when clicking the menu link.
function isAdminRole(user: { role?: string } | null | undefined): boolean {
  const r = normalizedRole(user?.role);
  return r === 'tenantadmin' || r === 'admin';
}

// Setup's old "Change" links open the Settings row instead of a wizard step.
function GettingStartedRoute() {
  const { search } = useLocation();
  const to = setupEditTarget(search);
  return to ? <Navigate to={to} replace /> : <OnboardingWizardPage />;
}

function BriefRoute() {
  const { user } = useAuth();
  // PlatformAdmin / TenantAdmin / generic Admin don't have an
  // Executive Brief — they have operational dashboards instead.
  // Anyone non-Executive landing here gets bounced via "/" so
  // RoleBasedHome picks the right surface for their role.
  if (isPlatformAdmin(user) || isAdminRole(user)) {
    return <Navigate to="/" replace />;
  }
  return <BriefPage />;
}

function RoleBasedHome() {
  const { user } = useAuth();
  const { variant } = useDashboardVariant();
  const navigationType = useNavigationType();
  const newToday = useNewToday();
  const todayPrefs = useTodayPrefs();
  // How an assistant came to "/", decided when they arrive: a later change of
  // query on the page (the setup drawer tidying ?setup=) isn't an arrival.
  const location = useLocation();
  const [cameByLink] = useState(() => navigationType === 'PUSH'
    || (navigationType === 'POP' && location.key !== 'default')
    || !!(location.state as { openToday?: boolean } | null)?.openToday);
  // A guided-setup link (`/?setup=next`, from setup's last step or the
  // emailed link) opens a step drawer on the workspace; don't send it via
  // The Brief, which would drop the query.
  // SetupProvider drops the param once it opens the drawer and leaves
  // `setupLink` in the history state, so the page stays put afterwards.
  // The morning email's "Still doing these?" link lands on the dashboard's list.
  const opensSetup = new URLSearchParams(location.search).has('setup')
    || new URLSearchParams(location.search).has('still-doing')
    || !!(location.state as { setupLink?: boolean } | null)?.setupLink;
  // Guided first-run gate (spec §0). The onboarding status is bootstrapped from
  // /auth/me after login: `undefined` = not yet resolved, `null` = not onboarded,
  // a date string = done. We must WAIT on `undefined` before any role routing —
  // otherwise an Executive would be sent to /brief before we know they still
  // need setup. `null` → setup. (Existing users were backfilled, so they resolve
  // to a date and only see a sub-second loader on the first home view.)
  const onboardingStatus = (user as { onboardingCompletedAt?: string | null } | null)?.onboardingCompletedAt;
  if (user && onboardingStatus === undefined) {
    return <LazyRouteFallback />;
  }
  if (user && onboardingStatus === null) {
    return <Navigate to="/getting-started" replace />;
  }
  // Platform admins manage tenants — they never get the Brief.
  if (isPlatformAdmin(user)) return <PlatformOverviewPage />;
  // An assistant's home is Executives (web app v4 plan §5.1): opening Cadence
  // at "/" (signing in, a bookmark, a redirect) lands there, keeping any
  // ?setup= so the guided-setup drawer still opens. Today in the nav, Back to
  // it, or a link to the plan shows their executive's day (Phase 4.5).
  if (normalizedRole(user?.role) === 'executiveassistant') {
    if (!cameByLink) {
      return <Navigate to={{ pathname: '/assistant', search: location.search, hash: location.hash }} state={location.state} replace />;
    }
    return <TodayPage />;
  }
  // Whether they're trying the new Today, and the day they last read the
  // brief, are the person's server preferences: wait for them.
  if (newToday === 'loading') return <LazyRouteFallback />;
  if (newToday) return <TodayPage />;
  // Tenant admins / generic Admins skip the Brief too — they need the
  // operational dashboard, not the cinematic landing. Only Executives
  // get the Brief moment.
  if (isAdminRole(user)) {
    return variant === 'briefing' ? <DashboardBriefingPage /> : <DashboardPage />;
  }
  // Executives land on The Brief once per day, until they've read it: the
  // day they last did is a server preference, so reading it in one browser
  // counts in all. If it couldn't be read, skip the Brief rather than loop.
  const briefRead = todayPrefs.failed || todayPrefs.prefs.briefFoldedOn === personToday();
  if (!briefRead && !opensSetup) return <Navigate to="/brief" replace />;
  return variant === 'briefing' ? <DashboardBriefingPage /> : <DashboardPage />;
}

function LazyRouteFallback() {
  return (
    <div className="flex min-h-[200px] items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );
}

function ProtectedApp() {
  return (
    <AuthProvider>
      {/* Plan entitlements load once per sign-in, alongside /auth/me, and
          decide which nav items and pages the tenant sees; the user's own
          permissions then hide what an admin has set to No. */}
      <EntitlementsProvider>
      <PermissionsProvider>
      <PrincipalContextProvider>
      <AuthGate>
        <Suspense fallback={<LazyRouteFallback />}>
        <Routes>
          <Route element={<AppLayout />}>
            <Route path="/" element={<RoleBasedHome />} />
            <Route path="/mfa-setup" element={<MfaSetupPage />} />
            <Route path="/getting-started" element={<GettingStartedRoute />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/:section" element={<SettingsPage />} />
            <Route path="/plan" element={<OldTodayOr old={<UnifiedPlanPage />} to="/" />} />
            <Route path="/focus" element={<FocusPage />} />
            <Route path="/focus/:view" element={<FocusPage />} />
            <Route path="/close-day" element={<OldTodayOr old={<CloseDayPage />} to="/?close=1" />} />
            <Route path="/work" element={<WorkPage />} />
            <Route path="/calendar" element={<CalendarPage />} />
            <Route path="/drivers" element={<DriversPage />} />
            <Route path="/drivers/:view" element={<DriversPage />} />
            <Route path="/projects" element={<ProjectsPage />} />
            <Route path="/projects/:projectId" element={<ProjectDetailPage />} />
            <Route path="/review" element={<ReviewPage />} />
            <Route path="/review/:section" element={<ReviewPage />} />
            <Route path="/insights" element={<InsightsPage />} />
            <Route path="/insights/:tab" element={<InsightsPage />} />
            <Route path="/assistant" element={<EaLandingPage />} />
            <Route path="/assistant/workspace" element={<EAWorkspacePage />} />
            <Route path="/assistant/profiles" element={<ExecutiveProfilesPage />} />
            <Route path="/assistant/profiles/:execId" element={<ExecutiveProfilesPage />} />
            <Route path="/decisions" element={<DecisionsPage />} />
            <Route path="/feature-requests" element={<FeatureRequestPage />} />
            <Route path="/organisation" element={<OrganisationPage />} />
            <Route path="/organisation/:section" element={<OrganisationPage />} />
            <Route path="/platform" element={<PlatformOverviewPage />} />
            <Route path="/platform/tenants" element={<PlatformTenantsPage />} />
            <Route path="/platform/products" element={<PlatformProductsPage />} />
            <Route path="/platform/email" element={<PlatformEmailPage />} />
            <Route path="/platform/security" element={<PlatformSecurityPage />} />
            <Route path="/platform/bug-reports" element={<PlatformBugReportsPage />} />
            <Route path="/platform/billing" element={<PlatformBillingPage />} />
            <Route path="/platform/releases" element={<PlatformReleasesPage />} />
          </Route>
          {/* The Brief: rendered outside AppLayout so the cinematic
              dark moment fills the viewport without the sidebar +
              top-bar chrome competing for attention. AuthGate still
              gates on a valid session. PlatformAdmin / TenantAdmin
              never see the Brief — it's an Executive moment — so any
              admin who lands here (deep link, cached URL, stale tab)
              gets bounced to the role-appropriate home via /. */}
          <Route path="/brief" element={<BriefRoute />} />
          {/* OAuth authorize: rendered without AppLayout chrome so the consent
              screen is the only thing the user sees. AuthGate above still gates
              on a valid Cadence session — login round-trips back here on success. */}
          <Route path="/oauth/authorize" element={<OAuthAuthorizePage />} />
          {/* Desktop sign-in consent: opened by the Mac / Windows app inside
              an OS-managed browser session. AuthGate above gates on a valid
              Cadence session — login (and MFA) round-trip back here, so by
              the time this renders the user is signed in and we can mint a
              one-time code for the desktop app to exchange. */}
          <Route path="/oauth/desktop" element={<DesktopAuthConsentPage />} />
          {/* Every old address, kept for good (navigation/legacyRedirects). */}
          {legacyRedirectRoutes()}
          {/* Catch /login and any unknown routes — redirect to dashboard */}
          <Route path="/login" element={<Navigate to="/" replace />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Suspense>
      </AuthGate>
      </PrincipalContextProvider>
      </PermissionsProvider>
      </EntitlementsProvider>
    </AuthProvider>
  );
}

function AppRouter() {
  // Use React-Router's location so soft navigation (Link / navigate) re-runs
  // this branch decision. Previously this read `window.location.pathname`
  // once at first render, which meant clicking "Back to sign in" from
  // /reset-password or /forgot-password kept the public-auth `<Routes>`
  // subtree mounted and rendered nothing for /login (blank white screen).
  const { pathname } = useLocation();

  // Public routes render WITHOUT the AuthGate redirect, but they still
  // need AuthProvider in the tree so `useAuth()` works — SignupPage and
  // the callback pages call `applyTokens` to sign the user in once their
  // flow completes, and `useAuth()` throws when no provider is mounted.
  // The `dark` class on the wrapper gives the shadcn primitives in
  // these pages a dark palette regardless of the user's saved preference.
  if (pathname === '/signup' || pathname.startsWith('/onboarding') || pathname.startsWith('/docs/') || pathname === '/auth/callback' || pathname === '/auth/magic-link' || pathname === '/auth/confirm-microsoft' || pathname === '/reset-password' || pathname === '/forgot-password' || pathname === '/join' || pathname === '/welcome') {
    return (
      <AuthProvider>
        <div className="dark">
          <Routes>
            <Route path="/signup" element={<SignupPage />} />
            <Route path="/onboarding/success" element={<OnboardingSuccessPage />} />
            <Route path="/auth/callback" element={<OAuthCallback />} />
            <Route path="/auth/magic-link" element={<MagicLinkCallback />} />
            <Route path="/auth/confirm-microsoft" element={<ConfirmMicrosoftPage />} />
            <Route path="/reset-password" element={<PasswordResetPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/join" element={<Suspense fallback={null}><JoinPage /></Suspense>} />
            <Route path="/welcome" element={<WelcomePage />} />
            <Route path="/docs/api-key-deprecation" element={<ApiKeyDeprecationPage />} />
            {/* Anything else under these prefixes (an old /onboarding/… link)
                used to render nothing: send it home, where sign-in decides. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </AuthProvider>
    );
  }

  return <ProtectedApp />;
}

function App() {
  return (
    <CadenceThemeProvider>
      <BrowserRouter>
        {/* The last resort for the pages outside the app shell (sign-in,
            the Brief, the consent pages): a card instead of a blank screen. */}
        <ErrorBoundary>
          <AppRouter />
        </ErrorBoundary>
      </BrowserRouter>
    </CadenceThemeProvider>
  );
}

export default App;
